import type { ButtonHTMLAttributes, MouseEvent, ReactNode } from 'react';
import { forwardRef, useContext } from 'react';

import { useAudioTrigger } from '../../../controllers/audioController/hooks';
import { useModifiers } from '../../../styles/hooks/useModifiers';
import { PassiveComponentContext } from '../../context';
import { Badge, BadgeSize } from '../../controls/badge/Badge';
import { Icon, Icons } from '../../icons';
import { withTooltip } from '../../tooltip/withTooltip';

import styles from './gameButton.module.css';

export interface GameButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Icon to display on the button */
  icon?: keyof Icons;
  /** Whether the button is active or not */
  active?: boolean;
  /** Whether it has border or not */
  border?: boolean;
  /** Whether the button in an active state should pulse green */
  pulse?: boolean;
  /** A name of sound sample to be played */
  sound?: string;
  /** Label to display on the button */
  label?: string | ReactNode;
  /** Class name to apply to the label */
  labelClass?: string;
  /** Badge to display on the button */
  badge?: string | number;
  /** Whether the button should grow to fill its container */
  grow?: boolean;
}

/**
 * Game button is a component that is used to display a game control or a chip stack control.
 * Built on the base of: `<button></button>`
 *
 * ## CSS Custom Properties
 *
 * | Name                 | Default                    | Description                              |
 * |----------------------|----------------------------|------------------------------------------|
 * | `--borderBackground` | `--game-border-background` | Border color (or gradient) of the button |
 * */
export const GameButton = forwardRef<HTMLDivElement, GameButtonProps>(
  (
    {
      icon,
      active,
      border,
      pulse,
      label,
      labelClass,

      badge,
      grow,
      className,
      sound,
      onClick,
      ...rest
    },
    forwardedRef
  ) => {
    const isPassive = useContext(PassiveComponentContext);
    const mods = useModifiers(styles, 'gameButton', { active, border, pulse, grow, isPassive });
    const audioTrigger = useAudioTrigger();

    const handleClick = (event: MouseEvent<HTMLButtonElement>): void => {
      audioTrigger.play({ name: sound || 'menuClick' });
      onClick?.(event);
    };

    return (
      <button className={`${styles.gameButton} ${mods} ${className ?? ''}`} onClick={handleClick} {...rest}>
        <div className={styles.gameButton__innerWrapper} ref={forwardedRef}>
          <div className={styles.gameButton__border} />
          <div className={styles.gameButton__content}>
            {icon ? <Icon icon={icon} /> : null}
            {label ? <div className={`${styles.gameButton__label} ${labelClass ?? ''}`}>{label}</div> : null}
          </div>
        </div>
        {badge ? (
          <span className={styles.gameButton__badge}>
            <Badge size={BadgeSize.Medium} value={badge} grow="start" />
          </span>
        ) : null}
      </button>
    );
  }
);

GameButton.displayName = 'GameButton';

export const GameButtonWithTooltip = withTooltip(GameButton);

GameButtonWithTooltip.displayName = 'GameButtonWithTooltip';
